import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supportAPI } from '../../services/api';
import { MessageCircle, X, Search, Filter, AlertCircle, CheckCircle, Clock, Settings, User, Upload, Save, Send, Bold, Italic, Underline } from 'lucide-react';
import { format } from 'date-fns';
import ImageUpload from '../common/ImageUpload';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SERVER_BASE_URL = API_BASE_URL.replace('/api', '');

const getImageUrl = (photoUrl) => {
  if (!photoUrl) return null;
  if (photoUrl.startsWith('http')) return photoUrl;
  if (photoUrl.startsWith('/')) {
    return `${SERVER_BASE_URL}${photoUrl}`;
  }
  return `${SERVER_BASE_URL}/${photoUrl}`;
};

// Render formatted text: **bold**, *italic*, __underline__
const renderFormattedText = (text) => {
  if (!text) return text;
  const parts = [];
  let remaining = text;
  let key = 0;
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|__(.+?)__)/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[2]) {
      parts.push(<strong key={key++}>{match[2]}</strong>);
    } else if (match[3]) {
      parts.push(<em key={key++}>{match[3]}</em>);
    } else if (match[4]) {
      parts.push(<u key={key++}>{match[4]}</u>);
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : text;
};

const getPriorityBadge = (priority) => {
  if (priority >= 5) {
    return { label: 'High', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' };
  } else if (priority >= 3) {
    return { label: 'Medium', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' };
  } else {
    return { label: 'Low', color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300' };
  }
};

const SupportDashboard = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'open', 'closed'
  const [searchQuery, setSearchQuery] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [supportSettings, setSupportSettings] = useState({
    support_display_name: '',
    support_photo_url: '',
    name: '',
    photo_url: ''
  });
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [broadcastError, setBroadcastError] = useState('');
  const [showBroadcastConfirm, setShowBroadcastConfirm] = useState(false);
  const [broadcastRecipientCount, setBroadcastRecipientCount] = useState(0);
  const broadcastTextareaRef = useRef(null);

  const applyFormatting = useCallback((type) => {
    const textarea = broadcastTextareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = broadcastMessage.substring(start, end);
    const wrappers = { bold: '**', italic: '*', underline: '__' };
    const wrapper = wrappers[type];
    if (!wrapper) return;
    const before = broadcastMessage.substring(0, start);
    const after = broadcastMessage.substring(end);
    const newText = selectedText
      ? `${before}${wrapper}${selectedText}${wrapper}${after}`
      : `${before}${wrapper}text${wrapper}${after}`;
    setBroadcastMessage(newText);
    // Restore cursor position after state update
    setTimeout(() => {
      textarea.focus();
      if (selectedText) {
        textarea.setSelectionRange(start, end + wrapper.length * 2);
      } else {
        textarea.setSelectionRange(start + wrapper.length, start + wrapper.length + 4);
      }
    }, 0);
  }, [broadcastMessage]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoUploadError, setPhotoUploadError] = useState('');

  useEffect(() => {
    loadConversations();
    loadSupportSettings();
    // Poll for new conversations every 10 seconds
    const interval = setInterval(() => {
      loadConversations();
    }, 10000);
    return () => clearInterval(interval);
  }, [statusFilter]);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages();
      // Poll for new messages every 5 seconds
      const interval = setInterval(() => {
        loadMessages();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [selectedConversation]);

  useEffect(() => {
    filterConversations();
  }, [conversations, statusFilter, searchQuery]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? statusFilter : undefined;
      const response = await supportAPI.getConversations(params);
      setConversations(response.data.conversations || []);
      setError('');
    } catch (err) {
      console.error('Failed to load conversations:', err);
      setError(err.response?.data?.error || 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    if (!selectedConversation) return;
    try {
      const response = await supportAPI.getConversationMessages(selectedConversation.id);
      setMessages(response.data.messages || []);
    } catch (err) {
      console.error('Failed to load messages:', err);
    }
  };

  const filterConversations = () => {
    let filtered = conversations;

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(conv => conv.status === statusFilter);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(conv => {
        const requesterName = conv.requester?.name?.toLowerCase() || '';
        const requesterEmail = conv.requester?.email?.toLowerCase() || '';
        return requesterName.includes(query) || requesterEmail.includes(query);
      });
    }

    // Sort conversations: Unread messages first for 'All' and 'Open' tabs
    if (statusFilter === 'all' || statusFilter === 'open') {
      filtered.sort((a, b) => {
        // First, prioritize unread messages
        const aHasUnread = (a.unread_count || 0) > 0 ? 1 : 0;
        const bHasUnread = (b.unread_count || 0) > 0 ? 1 : 0;

        if (aHasUnread !== bHasUnread) {
          return bHasUnread - aHasUnread; // Unread first
        }

        // If both have unread (or both don't), sort by unread count descending
        if (aHasUnread && bHasUnread) {
          const unreadDiff = (b.unread_count || 0) - (a.unread_count || 0);
          if (unreadDiff !== 0) return unreadDiff;
        }

        // Then by priority (higher priority first)
        const priorityDiff = (b.priority || 0) - (a.priority || 0);
        if (priorityDiff !== 0) return priorityDiff;

        // Then by status (open before closed)
        if (a.status !== b.status) {
          return a.status === 'open' ? -1 : 1;
        }

        // Finally by last message time (most recent first)
        const aTime = new Date(a.last_message_at || a.created_at).getTime();
        const bTime = new Date(b.last_message_at || b.created_at).getTime();
        return bTime - aTime;
      });
    } else {
      // For 'Closed' tab, just sort by last message time
      filtered.sort((a, b) => {
        const aTime = new Date(a.last_message_at || a.created_at).getTime();
        const bTime = new Date(b.last_message_at || b.created_at).getTime();
        return bTime - aTime;
      });
    }

    setFilteredConversations(filtered);
  };

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
    setNewMessage('');
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending || !selectedConversation) return;

    const messageText = newMessage.trim();
    setNewMessage('');
    setSending(true);

    try {
      await supportAPI.sendMessage(selectedConversation.id, messageText);
      await loadMessages();
      await loadConversations(); // Refresh to update last_message_at
      setError('');
    } catch (err) {
      console.error('Failed to send message:', err);
      setError(err.response?.data?.error || 'Failed to send message');
      setNewMessage(messageText);
    } finally {
      setSending(false);
    }
  };

  const handleCloseConversation = async () => {
    if (!selectedConversation || selectedConversation.status === 'closed') return;

    if (!window.confirm('Are you sure you want to close this conversation?')) {
      return;
    }

    try {
      await supportAPI.closeConversation(selectedConversation.id);
      await loadConversations();
      setSelectedConversation(null);
      setMessages([]);
    } catch (err) {
      console.error('Failed to close conversation:', err);
      setError(err.response?.data?.error || 'Failed to close conversation');
    }
  };

  const formatMessageTime = (timestamp) => {
    try {
      return format(new Date(timestamp), 'MMM d, h:mm a');
    } catch {
      return '';
    }
  };

  const formatConversationTime = (timestamp) => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return format(date, 'MMM d');
    } catch {
      return '';
    }
  };

  const isOwnMessage = (message) => {
    if (message.sender_type === 'admin') {
      return message.sender_id === user.id;
    }
    if (message.sender_type === user.userType) {
      return message.sender_id === user.id;
    }
    return false;
  };

  const handleOpenBroadcast = async () => {
    setBroadcastMessage('');
    setBroadcastError('');
    setShowBroadcastModal(true);

    // Get recipient count (all active therapists)
    try {
      const response = await supportAPI.getConversations();
      const allConversations = response.data.conversations || [];
      // Count unique requesters
      const uniqueRequesters = new Set();
      allConversations.forEach(conv => {
        if (conv.requester_id && conv.requester_type) {
          uniqueRequesters.add(`${conv.requester_type}-${conv.requester_id}`);
        }
      });
      setBroadcastRecipientCount(uniqueRequesters.size);
    } catch (err) {
      console.error('Failed to get recipient count:', err);
      setBroadcastRecipientCount(0);
    }
  };

  const handleSendBroadcast = () => {
    if (!broadcastMessage.trim()) {
      setBroadcastError('Message cannot be empty');
      return;
    }
    if (broadcastMessage.trim().length > 500) {
      setBroadcastError('Message cannot exceed 500 characters');
      return;
    }
    setShowBroadcastConfirm(true);
  };

  const handleConfirmBroadcast = async () => {
    try {
      setBroadcastSending(true);
      setBroadcastError('');

      const response = await supportAPI.broadcastMessage(broadcastMessage.trim());

      // Success
      setShowBroadcastConfirm(false);
      setShowBroadcastModal(false);
      setBroadcastMessage('');

      // Reload conversations to show new messages
      await loadConversations();

      // Show success notification
      alert(`Broadcast sent successfully to ${response.data.sent_count} therapists!`);
    } catch (err) {
      console.error('Failed to send broadcast:', err);
      setBroadcastError(err.response?.data?.error || 'Failed to send broadcast message');
      setShowBroadcastConfirm(false);
    } finally {
      setBroadcastSending(false);
    }
  };

  const handleCancelBroadcast = () => {
    setShowBroadcastModal(false);
    setShowBroadcastConfirm(false);
    setBroadcastMessage('');
    setBroadcastError('');
  };

  const handlePhotoUpload = (photoUrl) => {
    // Update the support settings with the new photo URL
    setSupportSettings(prev => ({
      ...prev,
      support_photo_url: photoUrl
    }));
  };

  const handlePhotoDelete = () => {
    // Clear the support photo URL
    setSupportSettings(prev => ({
      ...prev,
      support_photo_url: ''
    }));
  };

  const loadSupportSettings = async () => {
    try {
      setLoadingSettings(true);
      const response = await supportAPI.getSupportSettings();
      setSupportSettings({
        support_display_name: response.data.support_display_name || '',
        support_photo_url: response.data.support_photo_url || '',
        name: response.data.name || '',
        photo_url: response.data.photo_url || ''
      });
      setSettingsError('');
    } catch (err) {
      console.error('Failed to load support settings:', err);
      setSettingsError(err.response?.data?.error || 'Failed to load settings');
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);
      setSettingsError('');
      await supportAPI.updateSupportSettings({
        support_display_name: supportSettings.support_display_name || null,
        support_photo_url: supportSettings.support_photo_url || null
      });
      await loadSupportSettings();
      setShowSettings(false);
      // Reload messages to reflect the new display name/photo
      if (selectedConversation) {
        loadMessages();
      }
    } catch (err) {
      console.error('Failed to save support settings:', err);
      setSettingsError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <div className="flex h-full bg-white dark:bg-dark-bg-secondary rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
      {/* Conversations list */}
      <div className="w-1/3 border-r border-gray-200 dark:border-dark-border flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-dark-border">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-primary-600 dark:text-dark-primary-500" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
                Support Conversations
              </h2>
            </div>
            <div className="flex items-center gap-2">
              {user?.userType === 'admin' && (
                <button
                  onClick={handleOpenBroadcast}
                  className="p-2 text-primary-600 dark:text-dark-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                  title="Broadcast to All Therapists"
                >
                  <Send className="h-5 w-5" />
                </button>
              )}
              <button
                onClick={() => setShowSettings(true)}
                className="p-2 text-gray-600 dark:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary rounded-lg transition-colors"
                title="Support Settings"
              >
                <Settings className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-bg-tertiary dark:text-dark-text-primary placeholder-gray-400 dark:placeholder-dark-text-tertiary text-sm"
            />
          </div>

          {/* Status filter */}
          <div className="flex gap-2">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1 text-xs font-medium rounded ${
                statusFilter === 'all'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-secondary hover:bg-gray-200 dark:hover:bg-dark-bg-secondary'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setStatusFilter('open')}
              className={`px-3 py-1 text-xs font-medium rounded flex items-center gap-1 ${
                statusFilter === 'open'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-secondary hover:bg-gray-200 dark:hover:bg-dark-bg-secondary'
              }`}
            >
              <Clock className="h-3 w-3" />
              Open
            </button>
            <button
              onClick={() => setStatusFilter('closed')}
              className={`px-3 py-1 text-xs font-medium rounded flex items-center gap-1 ${
                statusFilter === 'closed'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-secondary hover:bg-gray-200 dark:hover:bg-dark-bg-secondary'
              }`}
            >
              <CheckCircle className="h-3 w-3" />
              Closed
            </button>
          </div>
        </div>

        {/* Conversations list */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-gray-500 dark:text-dark-text-tertiary">
              Loading conversations...
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="p-4 text-center text-gray-500 dark:text-dark-text-tertiary">
              No conversations found
            </div>
          ) : (
            filteredConversations.map((conv) => {
              const priorityBadge = getPriorityBadge(conv.priority || 0);
              const requesterPhoto = getImageUrl(conv.requester?.photo_url);
              const isSelected = selectedConversation?.id === conv.id;

              return (
                <div
                  key={conv.id}
                  onClick={() => handleSelectConversation(conv)}
                  className={`p-4 border-b border-gray-200 dark:border-dark-border cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary ${
                    isSelected ? 'bg-primary-50 dark:bg-dark-bg-tertiary' : ''
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className="relative flex-shrink-0">
                        {requesterPhoto ? (
                          <img
                            src={requesterPhoto}
                            alt={conv.requester?.name}
                            className="h-10 w-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-dark-bg-tertiary flex items-center justify-center">
                            <MessageCircle className="h-5 w-5 text-gray-400 dark:text-dark-text-tertiary" />
                          </div>
                        )}
                        {conv.unread_count > 0 && (
                          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full h-5 w-5 flex items-center justify-center ring-2 ring-white dark:ring-dark-bg-secondary">
                            {conv.unread_count > 9 ? '9+' : conv.unread_count}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 dark:text-dark-text-primary truncate">
                          {conv.requester?.name || 'Unknown'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-dark-text-tertiary truncate">
                          {conv.requester?.email || ''}
                        </div>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded flex-shrink-0 ${priorityBadge.color}`}>
                      {priorityBadge.label}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-dark-text-tertiary">
                    <span className={conv.status === 'open' ? 'text-green-600 dark:text-green-400' : ''}>
                      {conv.status === 'open' ? 'Open' : 'Closed'}
                    </span>
                    <span>{formatConversationTime(conv.last_message_at || conv.created_at)}</span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Chat view */}
      <div className="flex-1 flex flex-col">
        {selectedConversation ? (
          <>
            {/* Chat header */}
            <div className="p-4 border-b border-gray-200 dark:border-dark-border flex items-center justify-between">
              <div className="flex items-center gap-3">
                {getImageUrl(selectedConversation.requester?.photo_url) ? (
                  <img
                    src={getImageUrl(selectedConversation.requester?.photo_url)}
                    alt={selectedConversation.requester?.name}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-dark-bg-tertiary flex items-center justify-center">
                    <MessageCircle className="h-5 w-5 text-gray-400 dark:text-dark-text-tertiary" />
                  </div>
                )}
                <div>
                  <div className="font-medium text-gray-900 dark:text-dark-text-primary">
                    {selectedConversation.requester?.name || 'Unknown'}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-dark-text-tertiary">
                    {selectedConversation.requester?.email || ''}
                  </div>
                </div>
                {selectedConversation.plan_name && (
                  <span className="ml-2 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded">
                    {selectedConversation.plan_name}
                  </span>
                )}
              </div>
              {selectedConversation.status === 'open' && (
                <button
                  onClick={handleCloseConversation}
                  className="px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg flex items-center gap-2"
                >
                  <X className="h-4 w-4" />
                  Close
                </button>
              )}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => {
                const own = isOwnMessage(message);
                const senderPhoto = getImageUrl(message.sender_photo_url);
                const senderName = message.sender_name || 'Support Team';

                return (
                  <div key={message.id} className={`flex gap-3 ${own ? 'flex-row-reverse' : ''}`}>
                    <div className="flex-shrink-0">
                      {senderPhoto ? (
                        <img
                          src={senderPhoto}
                          alt={senderName}
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-primary-100 dark:bg-dark-primary-900 flex items-center justify-center">
                          <MessageCircle className="h-5 w-5 text-primary-600 dark:text-dark-primary-500" />
                        </div>
                      )}
                    </div>
                    <div className={`flex-1 ${own ? 'flex flex-col items-end' : ''}`}>
                      <div
                        className={`inline-block max-w-md px-4 py-2 rounded-lg ${
                          own
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary'
                        }`}
                      >
                        {!own && (
                          <div className="flex items-center gap-2 mb-1">
                            <div className="text-xs font-medium opacity-75 dark:opacity-90">{senderName}</div>
                            {message.is_broadcast && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                Broadcast
                              </span>
                            )}
                          </div>
                        )}
                        {own && message.is_broadcast && (
                          <div className="flex justify-end mb-1">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-white/20 text-white">
                              Broadcast
                            </span>
                          </div>
                        )}
                        <p className="whitespace-pre-wrap break-words">{renderFormattedText(message.message)}</p>
                      </div>
                      <div className={`text-xs text-gray-500 dark:text-dark-text-tertiary mt-1 ${own ? 'text-right' : ''}`}>
                        {formatMessageTime(message.created_at)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Message input */}
            {selectedConversation.status === 'open' && (
              <div className="p-4 border-t border-gray-200 dark:border-dark-border">
                {error && (
                  <div className="mb-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-800 dark:text-red-200">
                    {error}
                  </div>
                )}
                <form onSubmit={handleSendMessage} className="flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-bg-tertiary dark:text-dark-text-primary placeholder-gray-400 dark:placeholder-dark-text-tertiary"
                    disabled={sending}
                  />
                  <button
                    type="submit"
                    disabled={!newMessage.trim() || sending}
                    className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Send
                  </button>
                </form>
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-dark-text-tertiary">
            <div className="text-center">
              <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Select a conversation to view messages</p>
            </div>
          </div>
        )}
      </div>

      {/* Support Settings Modal */}
      {showSettings && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" 
          onClick={() => setShowSettings(false)}
        >
          <div 
            className="bg-white dark:bg-dark-bg-tertiary rounded-lg shadow-xl max-w-md w-full p-6" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
                Support Chat Settings
              </h3>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600 dark:text-dark-text-secondary mb-4">
              Set a custom display name and photo that will be shown to users when you reply to support chats.
            </p>

            {settingsError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-800 dark:text-red-200">
                {settingsError}
              </div>
            )}

            <div className="space-y-4">
              {/* Current Display Info */}
              <div className="p-3 bg-gray-50 dark:bg-dark-bg-secondary rounded-lg">
                <div className="text-xs font-medium text-gray-500 dark:text-dark-text-tertiary mb-2">
                  Current Profile (Fallback)
                </div>
                <div className="flex items-center gap-3">
                  {supportSettings.photo_url && getImageUrl(supportSettings.photo_url) ? (
                    <img
                      src={getImageUrl(supportSettings.photo_url)}
                      alt={supportSettings.name}
                      className="h-10 w-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-dark-bg-tertiary flex items-center justify-center">
                      <User className="h-5 w-5 text-gray-400 dark:text-dark-text-tertiary" />
                    </div>
                  )}
                  <div>
                    <div className="font-medium text-gray-900 dark:text-dark-text-primary">
                      {supportSettings.name || 'No name'}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-dark-text-tertiary">
                      {supportSettings.photo_url 
                        ? 'Used if support display name/photo not set'
                        : 'No profile photo available'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Support Display Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                  Support Display Name
                </label>
                <input
                  type="text"
                  value={supportSettings.support_display_name}
                  onChange={(e) => setSupportSettings({ ...supportSettings, support_display_name: e.target.value })}
                  placeholder={supportSettings.name || 'Enter display name'}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-bg-tertiary dark:text-dark-text-primary placeholder-gray-400 dark:placeholder-dark-text-tertiary"
                  disabled={loadingSettings || savingSettings}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-dark-text-tertiary">
                  Leave empty to use your regular name
                </p>
              </div>

              {/* Support Photo Upload */}
              <div>
                <ImageUpload
                  currentImageUrl={supportSettings.support_photo_url}
                  onUpload={handlePhotoUpload}
                  onDelete={handlePhotoDelete}
                  label="Support Photo"
                  userType={user.userType}
                  userId={user.id}
                  fieldName="support_photo_url"
                  disabled={loadingSettings || savingSettings}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-dark-text-tertiary">
                  Upload a custom photo for support chat. Leave empty to use your regular profile photo.
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowSettings(false)}
                className="flex-1 px-4 py-2 text-gray-700 dark:text-dark-text-secondary bg-gray-100 dark:bg-dark-bg-secondary rounded-lg hover:bg-gray-200 dark:hover:bg-dark-bg-tertiary transition-colors"
                disabled={savingSettings}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSettings}
                disabled={loadingSettings || savingSettings}
                className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {savingSettings ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Save Settings
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Broadcast Message Modal */}
      {showBroadcastModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={handleCancelBroadcast}
        >
          <div
            className="bg-white dark:bg-dark-bg-tertiary rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
                Broadcast to All Therapists
              </h3>
              <button
                onClick={handleCancelBroadcast}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                disabled={broadcastSending}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600 dark:text-dark-text-secondary mb-4">
              Send a message to all active therapists. This will create or add to their support conversations.
            </p>

            {broadcastError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-800 dark:text-red-200">
                {broadcastError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                  Message (max 500 characters)
                </label>
                <div className="flex items-center gap-1 mb-1 p-1 bg-gray-50 dark:bg-dark-bg-secondary rounded-t-lg border border-b-0 border-gray-300 dark:border-dark-border">
                  <button
                    type="button"
                    onClick={() => applyFormatting('bold')}
                    className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-dark-bg-tertiary text-gray-600 dark:text-dark-text-secondary transition-colors"
                    title="Bold (**text**)"
                    disabled={broadcastSending}
                  >
                    <Bold className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormatting('italic')}
                    className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-dark-bg-tertiary text-gray-600 dark:text-dark-text-secondary transition-colors"
                    title="Italic (*text*)"
                    disabled={broadcastSending}
                  >
                    <Italic className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormatting('underline')}
                    className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-dark-bg-tertiary text-gray-600 dark:text-dark-text-secondary transition-colors"
                    title="Underline (__text__)"
                    disabled={broadcastSending}
                  >
                    <Underline className="h-4 w-4" />
                  </button>
                  <span className="text-xs text-gray-400 dark:text-dark-text-tertiary ml-2">Select text then click to format</span>
                </div>
                <textarea
                  ref={broadcastTextareaRef}
                  value={broadcastMessage}
                  onChange={(e) => setBroadcastMessage(e.target.value)}
                  placeholder="Type your broadcast message... Use **bold**, *italic*, __underline__"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-b-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-bg-secondary dark:text-dark-text-primary placeholder-gray-400 dark:placeholder-dark-text-tertiary resize-none"
                  rows={6}
                  disabled={broadcastSending}
                  maxLength={500}
                />
                <div className="flex justify-between mt-1 text-xs">
                  <span className={`${
                    broadcastMessage.length > 500
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-gray-500 dark:text-dark-text-tertiary'
                  }`}>
                    {broadcastMessage.length} / 500 characters
                  </span>
                </div>
                {broadcastMessage.trim() && (
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-gray-500 dark:text-dark-text-tertiary mb-1">Preview</label>
                    <div className="p-3 bg-gray-50 dark:bg-dark-bg-secondary rounded-lg border border-gray-200 dark:border-dark-border">
                      <p className="text-sm text-gray-700 dark:text-dark-text-primary whitespace-pre-wrap">{renderFormattedText(broadcastMessage)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleCancelBroadcast}
                className="flex-1 px-4 py-2 text-gray-700 dark:text-dark-text-secondary bg-gray-100 dark:bg-dark-bg-secondary rounded-lg hover:bg-gray-200 dark:hover:bg-dark-bg-tertiary transition-colors"
                disabled={broadcastSending}
              >
                Cancel
              </button>
              <button
                onClick={handleSendBroadcast}
                disabled={broadcastSending || !broadcastMessage.trim() || broadcastMessage.length > 500}
                className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Send className="h-4 w-4" />
                Send Broadcast
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Broadcast Confirmation Modal */}
      {showBroadcastConfirm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => !broadcastSending && setShowBroadcastConfirm(false)}
        >
          <div
            className="bg-white dark:bg-dark-bg-tertiary rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
                Confirm Broadcast
              </h3>
            </div>

            <p className="text-sm text-gray-600 dark:text-dark-text-secondary mb-4">
              Are you sure you want to send this message to approximately <strong>{broadcastRecipientCount}</strong> therapists?
            </p>

            <div className="p-3 bg-gray-50 dark:bg-dark-bg-secondary rounded-lg mb-4">
              <p className="text-sm text-gray-700 dark:text-dark-text-primary whitespace-pre-wrap">
                {renderFormattedText(broadcastMessage)}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowBroadcastConfirm(false)}
                className="flex-1 px-4 py-2 text-gray-700 dark:text-dark-text-secondary bg-gray-100 dark:bg-dark-bg-secondary rounded-lg hover:bg-gray-200 dark:hover:bg-dark-bg-tertiary transition-colors"
                disabled={broadcastSending}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmBroadcast}
                disabled={broadcastSending}
                className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {broadcastSending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Confirm & Send
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportDashboard;

